/**
 * The closed vocabularies behind coverage's country and language fields, with the codes the
 * warehouse actually stamps on a hit.
 *
 * The editor offers names ("United States", "English") and stores what was picked; the warehouse
 * tags every hit with ISO codes (`hits.country = 'US'`, `hits.language = 'en'`). Until 2026-09-16
 * the two were compared literally, so a report allowing "United States" allowed nothing at all
 * — and every migrated report carries exactly that rule. Matching canonicalises both sides
 * through these tables: a rule may be written as the name or the code, in any case, and an
 * unknown value still compares as itself so free entry keeps working.
 */
export const COUNTRY_VOCAB: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'PL', name: 'Poland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'EE', name: 'Estonia' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'IL', name: 'Israel' },
  { code: 'TR', name: 'Turkey' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
]

export const LANGUAGE_VOCAB: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'it', name: 'Italian' },
  { code: 'zh', name: 'Mandarin' },
  { code: 'ko', name: 'Korean' },
]

/** The names the editor offers, in the order the pickers show them. */
export const COUNTRIES: string[] = COUNTRY_VOCAB.map((entry) => entry.name)
export const LANGUAGES: string[] = LANGUAGE_VOCAB.map((entry) => entry.name)

const lookup = (vocab: ReadonlyArray<{ code: string; name: string }>) => {
  const byKey = new Map<string, string>()
  for (const { code, name } of vocab) {
    byKey.set(code.toLowerCase(), code)
    byKey.set(name.toLowerCase(), code)
  }
  return byKey
}
const COUNTRY_BY_KEY = lookup(COUNTRY_VOCAB)
const LANGUAGE_BY_KEY = lookup(LANGUAGE_VOCAB)
// The legacy portal's country spellings that differ from ours; a migrated rule may carry them.
const COUNTRY_ALIASES: Record<string, string> = {
  usa: 'US',
  'united states of america': 'US',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  czechia: 'CZ',
  'korea, republic of': 'KR',
  'republic of korea': 'KR',
  'russian federation': 'RU',
  'viet nam': 'VN',
  uae: 'AE',
}

/**
 * The ISO 3166-1 alpha-2 code for a country given as a code or a name, uppercased; an unknown
 * value comes back trimmed and uppercased so it still compares with itself.
 */
export function canonicalCountry(value: string): string {
  const key = value.trim().toLowerCase()
  return COUNTRY_BY_KEY.get(key) ?? COUNTRY_ALIASES[key] ?? key.toUpperCase()
}

/**
 * The ISO 639-1 base tag for a language given as a code (`pt-BR` → `pt`) or a name, lowercased;
 * an unknown value comes back as its own base tag.
 */
export function canonicalLanguage(value: string): string {
  const key = value.trim().toLowerCase()
  return LANGUAGE_BY_KEY.get(key) ?? key.split('-')[0] ?? ''
}

/** The display name for a stored value, whichever form it was stored in. */
export function countryLabel(value: string): string {
  const code = canonicalCountry(value)
  return COUNTRY_VOCAB.find((entry) => entry.code === code)?.name ?? value
}

export function languageLabel(value: string): string {
  const code = canonicalLanguage(value)
  return LANGUAGE_VOCAB.find((entry) => entry.code === code)?.name ?? value
}
